"use client";
import React, { useEffect, useState } from "react";
import type { CategoryNode } from "@/types/pim";
import { Select } from "antd";
import { useIsMobile } from "../hooks/useIsMobile";
import { useInventoryFilters } from "./InventoryFiltersContext";

const LOCATION = "Location";

const normalizeSelection = (value: any): string[] =>
  Array.isArray(value) ? value : value ? [value] : [];

/**
 * Full-width Location filter shown above the inventory grid (buttons on desktop,
 * multi-select dropdown on mobile). Mirrors the sidebar's old Location attribute
 * filter: it reads available Location values (warehouse code / abbreviation /
 * count) from the same /attributes/available endpoint and writes selections to
 * the shared "Location" URL param.
 */
const LocationFilter: React.FC<{
  category: CategoryNode | null;
  filters: Record<string, any>;
  searchQuery?: string;
  onFilterChange: (attributeName: string, value: any) => void;
}> = ({ filters, onFilterChange }) => {
  // Client-side breakpoint check (matchMedia) — the server no longer sniffs
  // user-agents, so SSR markup is identical for every device.
  const isMobile = useIsMobile();
  // Available Location values come from the server-computed filter data (no fetch).
  const { availableValues } = useInventoryFilters();
  const options = availableValues[LOCATION] || [];

  // Optimistic selection mirrors filters["Location"] so toggles feel instant
  const [optimistic, setOptimistic] = useState<string[]>(normalizeSelection(filters[LOCATION]));
  useEffect(() => {
    setOptimistic(normalizeSelection(filters[LOCATION]));
  }, [filters]);

  const applySelection = (next: string[]) => {
    setOptimistic(next);
    onFilterChange(LOCATION, next.length > 0 ? next : null);
  };

  const toggleLocation = (code: string) => {
    applySelection(
      optimistic.includes(code)
        ? optimistic.filter((c) => c !== code)
        : [...optimistic, code]
    );
  };

  if (options.length === 0) {
    return null;
  }

  const sorted = [...options].sort((a, b) =>
    String(a.value ?? '').localeCompare(String(b.value ?? ''))
  );

  return (
    <div className="bg-white border-b border-gray-200 text-black">
      {/* Desktop: full-width row of toggle buttons */}
      <div className="hidden lg:flex items-center gap-2 flex-wrap pb-2 px-3">
        {sorted.map((opt) => {
          const code = opt.code ?? String(opt.value);
          const isSelected = optimistic.includes(code);
          return (
            <button
              key={code}
              type="button"
              aria-pressed={isSelected}
              onClick={() => toggleLocation(code)}
              className={`hover:cursor-pointer shrink-0 px-4 py-2 text-sm font-medium rounded-lg border transition-colors whitespace-nowrap ${isSelected
                ? 'bg-gcopper text-white border-gcopper'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100 active:bg-gray-200'
                }`}
            >
              {opt.value} ({opt.count ?? 0})
            </button>
          );
        })}
      </div>

      {/* Mobile: multi-select dropdown */}
      <div className="lg:hidden px-3 pb-2 pt-1">
        <label className="block text-sm font-semibold text-gray-700 mb-1">Location</label>
        <Select
          mode="multiple"
          className="w-full"
          placeholder="Select locations"
          showSearch={!isMobile}
          value={optimistic}
          onChange={(values: string[]) => applySelection(values)}
          maxTagCount="responsive"
          options={sorted.map((opt) => ({
            label: `${opt.value} (${opt.count ?? 0})`,
            value: opt.code ?? String(opt.value),
          }))}
          tagRender={(props) => {
            const { label, closable, onClose } = props;
            const displayLabel = typeof label === 'string' ? label.split(' (')[0] : label;
            return (
              <span className="ant-select-selection-item" style={{ display: 'inline-flex', alignItems: 'center' }}>
                <span className="ant-select-selection-item-content">{displayLabel}</span>
                {closable && (
                  <span
                    className="ant-select-selection-item-remove"
                    onClick={onClose}
                    style={{ marginLeft: '4px', cursor: 'pointer' }}
                  >
                    ×
                  </span>
                )}
              </span>
            );
          }}
        />
      </div>
    </div>
  );
};

export default LocationFilter;
